import React, { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import GroupsIcon from '@mui/icons-material/Groups';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import SettingsIcon from '@mui/icons-material/Settings';
import TuneIcon from '@mui/icons-material/Tune';
import { collection, getDocs, query, where } from 'firebase/firestore';

import { db } from '../../firebaseConfig';
import { useTenant } from '../../contexts/TenantContext';
import {
  buildActivityText,
  formatLocalDate,
  loadAdminBaseData,
  loadAppointmentsForDate,
  loadRecentActivities,
} from '../../utils/adminAppointments';

const formatGermanDate = (dateString) => {
  if (!dateString) return '';
  return new Date(`${dateString}T00:00:00`).toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

const DashboardCard = ({ icon, label, value, helper }) => (
  <Paper sx={{ p: 2.5, borderRadius: 2, height: '100%' }}>
    <Stack direction="row" spacing={2} alignItems="center">
      <Box sx={{ color: 'primary.main', display: 'flex' }}>{icon}</Box>
      <Box>
        <Typography variant="h4" sx={{ fontWeight: 700, color: '#18212f' }}>
          {value}
        </Typography>
        <Typography variant="body2" color="text.secondary">{label}</Typography>
        {helper && <Typography variant="caption" color="text.secondary">{helper}</Typography>}
      </Box>
    </Stack>
  </Paper>
);

const AdminDashboard = () => {
  const { tenantId } = useTenant();
  const [employees, setEmployees] = useState([]);
  const [appointmentTypes, setAppointmentTypes] = useState([]);
  const [todayAppointments, setTodayAppointments] = useState([]);
  const [upcomingAppointments, setUpcomingAppointments] = useState([]);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const today = formatLocalDate();

  useEffect(() => {
    if (!tenantId) return;

    const loadDashboard = async () => {
      setLoading(true);
      try {
        const upcomingQuery = query(
          collection(db, 'tenants', tenantId, 'appointments'),
          where('date', '>=', today)
        );

        const [
          { employees: loadedEmployees, appointmentTypes: loadedTypes },
          loadedTodayAppointments,
          upcomingSnap,
          recentActivities,
        ] = await Promise.all([
          loadAdminBaseData(tenantId),
          loadAppointmentsForDate(tenantId, today),
          getDocs(upcomingQuery),
          loadRecentActivities(tenantId, 10),
        ]);

        setEmployees(loadedEmployees);
        setAppointmentTypes(loadedTypes);
        setTodayAppointments(loadedTodayAppointments);
        setUpcomingAppointments(upcomingSnap.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() })));
        setActivities(recentActivities);
        setError(null);
      } catch (err) {
        console.error(err);
        setError('Dashboard konnte nicht geladen werden.');
      } finally {
        setLoading(false);
      }
    };

    loadDashboard();
  }, [tenantId, today]);

  const activeToday = todayAppointments.filter((appointment) => appointment.canceled !== true);
  const canceledToday = todayAppointments.filter((appointment) => appointment.canceled === true);

  const nextAppointments = useMemo(
    () =>
      upcomingAppointments
        .filter((appointment) => appointment.canceled !== true)
        .sort((a, b) => `${a.date} ${a.startTime}`.localeCompare(`${b.date} ${b.startTime}`))
        .slice(0, 5),
    [upcomingAppointments]
  );

  const employeeMap = useMemo(
    () => Object.fromEntries(employees.map((employee) => [employee.id, employee])),
    [employees]
  );

  const typeMap = useMemo(
    () => Object.fromEntries(appointmentTypes.map((type) => [type.id, type])),
    [appointmentTypes]
  );

  const quickActions = [
    { label: 'Termin anlegen', path: '/schedule?create=1', icon: <AddIcon /> },
    { label: 'Tagesplan öffnen', path: '/schedule', icon: <CalendarTodayIcon /> },
    { label: 'Verfügbarkeit pflegen', path: '/manage-availability', icon: <TuneIcon /> },
    { label: 'Terminarten', path: '/admin/appointment-types', icon: <SettingsIcon /> },
    { label: 'Mitarbeiter', path: '/admin/user-management', icon: <GroupsIcon /> },
  ];

  if (loading) {
    return <CircularProgress sx={{ display: 'block', margin: '32px auto' }} />;
  }

  return (
    <Box sx={{ bgcolor: '#f6f8fb', minHeight: '100vh', p: { xs: 2, md: 4 } }}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#18212f' }}>
            Admin Dashboard
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Heute im Blick: Termine, Auslastung und neue Ereignisse.
          </Typography>
        </Box>
        <Button component={RouterLink} to="/schedule?create=1" variant="contained" startIcon={<AddIcon />}>
          Telefonischen Termin eintragen
        </Button>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} lg={3}>
          <DashboardCard
            icon={<CalendarTodayIcon />}
            label="Termine heute"
            value={activeToday.length}
            helper={formatGermanDate(today)}
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <DashboardCard
            icon={<EventBusyIcon />}
            label="Stornos heute"
            value={canceledToday.length}
            helper="als Hinweis für Rückfragen"
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <DashboardCard
            icon={<GroupsIcon />}
            label="Mitarbeiter"
            value={employees.length}
            helper="aktiv im System"
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <DashboardCard
            icon={<SettingsIcon />}
            label="Terminarten"
            value={appointmentTypes.length}
            helper="buchbare Leistungen"
          />
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        <Grid item xs={12} lg={7}>
          <Paper sx={{ p: 2.5, borderRadius: 2 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
              <Typography variant="h6">Nächste Termine</Typography>
              <Button component={RouterLink} to="/schedule" size="small">Tagesplan</Button>
            </Stack>
            <Stack spacing={1.25}>
              {nextAppointments.length === 0 ? (
                <Alert severity="info">Keine kommenden Termine gefunden.</Alert>
              ) : (
                nextAppointments.map((appointment) => (
                  <Paper key={appointment.id} variant="outlined" sx={{ p: 1.5, borderRadius: 1.5 }}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1}>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          {formatGermanDate(appointment.date)} um {appointment.startTime} - {appointment.customerName || 'Ohne Namen'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {typeMap[appointment.appointmentTypeId]?.name || 'Termin'} bei{' '}
                          {employeeMap[appointment.employeeId]?.name || 'Unbekannt'}
                        </Typography>
                      </Box>
                      {appointment.bookingSource === 'admin' && <Chip label="intern" size="small" />}
                    </Stack>
                  </Paper>
                ))
              )}
            </Stack>
          </Paper>
        </Grid>

        <Grid item xs={12} lg={5}>
          <Paper sx={{ p: 2.5, borderRadius: 2, mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>Schnellzugriff</Typography>
            <Grid container spacing={1.5}>
              {quickActions.map((action) => (
                <Grid item xs={12} sm={6} key={action.label}>
                  <Button
                    component={RouterLink}
                    to={action.path}
                    variant="outlined"
                    fullWidth
                    startIcon={action.icon}
                    sx={{ justifyContent: 'flex-start', py: 1.25 }}
                  >
                    {action.label}
                  </Button>
                </Grid>
              ))}
            </Grid>
          </Paper>

          <Paper sx={{ p: 2.5, borderRadius: 2 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
              <NotificationsNoneIcon color="primary" />
              <Typography variant="h6">Nachrichten & Log</Typography>
            </Stack>
            <Stack spacing={1.25}>
              {activities.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  Noch keine Einträge. Neue Buchungen und Stornos erscheinen hier.
                </Typography>
              ) : (
                activities.map((activity) => (
                  <Box key={activity.id} sx={{ borderLeft: '3px solid', borderColor: 'primary.main', pl: 1.5 }}>
                    <Typography variant="body2">{activity.message || buildActivityText(activity)}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {activity.createdAt?.toDate ? activity.createdAt.toDate().toLocaleString('de-DE') : ''}
                    </Typography>
                  </Box>
                ))
              )}
            </Stack>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AdminDashboard;
